import React from 'react'
import {
  fetchUsers,
  patchUser,
  postUser,
  deleteUser,
  inviteUser,
} from 'api/users'
import { useDebounce, useModal, useSnackbar } from 'hooks'
import { MEMBER_ROLE, ADMIN_ROLE } from 'constants/users'
import { useDispatch, useSelector } from 'react-redux'
import { toUserDepartments, getLocation } from 'ducks/router'
import { t } from 'ttag'
import * as R from 'ramda'
import { mapInternalStateToPayload } from 'components/modals/ManageUserModal/actions'

const useMambers = ({ debouncedSearch, pk }) => {
  const [members, setMembers] = React.useState([])
  const [allMembers, setAllMembers] = React.useState([])

  const dispatch = useDispatch()

  const setUser = React.useCallback(
    userId => {
      dispatch(
        toUserDepartments({
          pk: userId,
          query: { limit: 15, offset: 0 },
        }),
      )
    },
    [dispatch, toUserDepartments],
  )

  const fetchMembers = async () => {
    const { response } = await fetchUsers({ search: debouncedSearch })

    return response.results
      .filter(member => [MEMBER_ROLE, ADMIN_ROLE].includes(member.role))
      .sort(member => (member.role === ADMIN_ROLE ? -1 : 1))
  }

  const sendInvite = async id => {
    const { response } = await inviteUser(id)

    return response
  }

  const getMembers = async (refetchAllMembers = false) => {
    const members = await fetchMembers()

    setMembers(members)

    if (refetchAllMembers) {
      setAllMembers(members)
    }
  }

  React.useEffect(getMembers, [debouncedSearch])

  React.useEffect(async () => {
    const members = await fetchMembers()

    setMembers(members)
    setAllMembers(members)

    const currentUser = pk || members[0]?.id

    if (!R.isNil(currentUser)) {
      setUser(currentUser)
    }
  }, [])

  const selectedUser = allMembers?.find(member => member.id === pk)

  return {
    user: pk,
    selectedUser,
    members,
    setUser,
    getMembers,
    sendInvite,
  }
}

const useDeleteUserModal = (state, handlers) => {
  const { selectedUser } = state
  const { handleDeleteUser, handleManageUserClose } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleDeleteUserConfirm = React.useCallback(() => {
    handleDeleteUser(
      { userId: selectedUser?.id },
      { postSuccessAction: handleClose },
    )
  }, [handleDeleteUser, selectedUser, handleClose])

  return {
    handleDeleteUserClose: handleClose,
    handleDeleteUserConfirm,
    handleDeleteUserOpen: () => {
      handleManageUserClose()
      handleOpen()
    },
    isDeleteUserOpen: isOpen,
  }
}

const useManageUserModal = (state, handlers) => {
  const [isEditMode, setIsEditMode] = React.useState()

  const { selectedUser } = state
  const { setUser, getMembers, sendInvite, handleShowSnackbar } = handlers

  const userId = selectedUser?.id

  const { handleClose, handleOpen, isOpen } = useModal()

  const dispatch = useDispatch()

  const { query = {} } = useSelector(getLocation)

  const handleConfirm = React.useCallback(
    async (data, { onError, onSuccess }) => {
      const payload = mapInternalStateToPayload(data, isEditMode)

      const { response, error, meta } = await (isEditMode
        ? patchUser(userId, payload)
        : postUser(payload))

      if (response) {
        if (!isEditMode && data.invite) {
          await sendInvite(response.id)
        }

        handleClose()
        onSuccess()
        await getMembers(true)

        if (!isEditMode) {
          setUser(response.id)
        }

        handleShowSnackbar({
          message: isEditMode
            ? t`User successfully edited`
            : t`User successfully added`,
          type: 'success',
        })
      } else {
        onError({ message: error, type: meta.HTTPstatus })
      }
    },
    [query, dispatch, handleClose, userId, isEditMode, sendInvite],
  )

  const handleManageUserOpen = isEditMode => {
    setIsEditMode(isEditMode)
    handleOpen()
  }

  return {
    handleManageUserClose: handleClose,
    handleManageUserConfirm: handleConfirm,
    handleManageUserOpen,
    isManageUserOpen: isOpen,
    isEditMode,
  }
}

export const usePanel = ({ pk }) => {
  const [search, setSearch] = React.useState('')
  const [isSearchFocused, setIsSearchFocused] = React.useState(false)

  const debouncedSearch = useDebounce(search, { delay: 300 })

  const {
    user,
    selectedUser,
    members,
    getMembers,
    setUser,
    sendInvite,
  } = useMambers({
    debouncedSearch,
    pk,
  })

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const handleResetUser = () => {
    const currentUser = members[0]?.id

    if (!R.isNil(currentUser)) {
      setUser(currentUser)
    }
  }

  const handleDeleteUser = async ({ userId }, { postSuccessAction } = {}) => {
    const { response } = await deleteUser(userId)

    if (response) {
      postSuccessAction()
      await getMembers(true)
      handleResetUser()
      handleShowSnackbar({
        message: t`User successfully removed`,
        type: 'success',
      })
    }
  }

  const {
    handleManageUserClose,
    handleManageUserConfirm,
    handleManageUserOpen,
    isManageUserOpen,
    isEditMode,
  } = useManageUserModal(
    { selectedUser },
    { setUser, getMembers, sendInvite, handleShowSnackbar },
  )

  const {
    handleDeleteUserClose,
    handleDeleteUserConfirm,
    handleDeleteUserOpen,
    isDeleteUserOpen,
  } = useDeleteUserModal(
    { selectedUser },
    { handleDeleteUser, handleResetUser, handleManageUserClose },
  )

  return {
    state: {
      user,
      search,
      members,
      isEditMode,
      selectedUser,
      isSearchFocused,
      isManageUserOpen,
      isDeleteUserOpen,
    },
    handlers: {
      setUser,
      setSearch,
      setIsSearchFocused,
      handleManageUserClose,
      handleManageUserConfirm,
      handleManageUserOpen,
      handleDeleteUserOpen,
      handleDeleteUserClose,
      handleDeleteUserConfirm,
    },
  }
}
